export interface WorkShift {
  startTime: string;
  endTime: string;
}

/**
 * Parse work shifts from an unknown Prisma JSON value.
 * Falls back to a default 09:00-18:00 shift when the value is missing,
 * not an array, or contains no valid shift objects.
 */
export const parseWorkShifts = (raw: unknown): WorkShift[] => {
  const fallback: WorkShift[] = [{ startTime: '09:00', endTime: '18:00' }];
  if (!raw) return fallback;
  if (!Array.isArray(raw)) return fallback;
  const shifts = raw.filter(
    (s): s is WorkShift =>
      typeof s === 'object' && s !== null &&
      typeof (s as Record<string, unknown>).startTime === 'string' &&
      typeof (s as Record<string, unknown>).endTime === 'string',
  );
  return shifts.length > 0 ? shifts : fallback;
};

/**
 * Convert a single work shift to its duration in minutes.
 * Assumes well-formed "HH:mm" strings.
 */
export const shiftToMinutes = (shift: WorkShift): number => {
  const [startH, startM] = shift.startTime.split(':').map(Number);
  const [endH, endM] = shift.endTime.split(':').map(Number);
  const start = startH * 60 + startM;
  const end = endH * 60 + endM;
  // Normalize midnight crossings: if end < start, the shift spans midnight
  // (e.g. 22:00 → 02:00), so add 24h to get the correct positive duration.
  return end < start ? end + 24 * 60 - start : end - start;
};

/**
 * Get the day of week (0-6, Sunday=0) for a "yyyy-MM-dd" date string
 * interpreted in the given IANA timezone. Falls back to UTC if the
 * timezone is invalid or unsupported.
 */
export const getDayOfWeekInTimezone = (dateStr: string, timezone: string): number => {
  try {
    // Use Intl to format the date in the target timezone and extract the weekday.
    // This avoids relying on the browser's local timezone.
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
    });
    const weekday = formatter.format(new Date(dateStr + 'T12:00:00Z'));
    const map: Record<string, number> = {
      Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
    };
    return map[weekday] ?? new Date(dateStr + 'T00:00:00').getDay();
  } catch {
    // Invalid timezone — fall back to browser local
    return new Date(dateStr + 'T00:00:00').getDay();
  }
};

/**
 * Calculate expected working minutes for a given date based on the user's
 * shifts and scheduled days of week.
 * Accepts either a Date object (uses browser local time for day-of-week) or
 * a date string with an optional timezone (uses Intl-based timezone conversion).
 */
export const expectedMinutesForDate = (
  date: Date | string,
  workShifts: WorkShift[],
  workScheduleDays: number[],
  timezone?: string,
): number => {
  const dayOfWeek = typeof date === 'string'
    ? getDayOfWeekInTimezone(date, timezone ?? 'UTC')
    : date.getDay();
  if (!workScheduleDays.includes(dayOfWeek)) return 0;
  return workShifts.reduce((sum, shift) => sum + shiftToMinutes(shift), 0);
};

/**
 * Format a duration expressed in minutes as a compact human-readable string.
 */
export const formatDurationFromMinutes = (minutes: number): string => {
  if (minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};
